import { useEffect, useMemo } from 'react'

import { useEndpoint } from '@surglogs-app/network'

import { usePermissionContext } from '../context/PermissionsContext'
import {
  getPermission,
  RequiredPermissionParams,
  AuthPermissionsLogicType,
  generateUniqueRequestedSrns,
  generateBaseClinicSrn,
  generateSrn,
  MATCH_ALL_RESOURCES_SIGN,
  PermissionAction,
} from '../helpers'
import { getPermissions } from '../helpers/api'
import { getModuleMapKey, parseModuleMapKey } from '../helpers/moduleMapKey'
import {
  hasAnyResourceInModuleWithViewPermission,
  hasContentPermission,
  hasSystemPermission,
} from '../helpers/permissionDecider'
import {
  AccessPermissionsResponse,
  generateMapForEmptySystemPermissions,
  generateNewMapOfContentPermissions,
  generateNewMapOfSystemPermissions,
} from '../helpers/permissionsDataTransformer'

const logIfEnabled =
  (enabled: boolean) =>
  (...args: any[]) => {
    if (enabled) {
      // eslint-disable-next-line no-console
      console.log('[auth]', ...args)
      return true
    }

    return false
  }

const checkPermissionGrantedThroughAllResourcesPermission = ({
  clinicId,
  askedPermission,
  data,
  getPermissionsForModule,
}: {
  clinicId: number
  askedPermission: RequiredPermissionParams
  data: AccessPermissionsResponse | undefined
  getPermissionsForModule: (
    clinicId: number,
    module: string,
  ) => Map<string, number[]> | undefined
}) => {
  const allResourcesPermission = {
    ...askedPermission,
    resourceId: MATCH_ALL_RESOURCES_SIGN,
  }
  const requiredPermission = getPermission(allResourcesPermission)

  return hasContentPermission({
    clinicId,
    askedPermission: allResourcesPermission,
    requiredPermission,
    data,
    getPermissionsForModule,
  })
}

/**
 * New version of useAuthPermissions which implements comprehensive access management
 *
 * NOTE:
 *  - This hook can verify content & system permission.
 *    (If you ask for system permission, dont provide "resourceId")
 *
 * @param clinicId provide clinic id into request for fetching resources
 * @param askedForPermissions what permissions/resources you want to verify
 * @param config options to configure behavior of this hook
 *  (default values provided, by default the user has access to resource you are asking for)
 * @returns boolean
 */
export const useRolesAuthPermissions = (
  clinicId: number,
  askedForPermissions: RequiredPermissionParams[],
  config?: {
    defaultsTo?: boolean
    skip?: boolean
    logic?: AuthPermissionsLogicType
    debug?: boolean
  },
) => {
  const {
    defaultsTo = true,
    skip = false,
    logic = AuthPermissionsLogicType.OR,
  } = config || {}

  const log = logIfEnabled(config?.debug ?? false)

  const {
    apiVersion,
    permissionsPerModuleMap,
    systemPermissions,
    addPermissionsForModule,
    getPermissionsForModule,
    addSystemPermissions,
    getSystemPermissionsForModule,
    resetPermissionsMapsIfNeeded,
  } = usePermissionContext()

  const requestedContentPermissionsModules = useMemo(
    () =>
      askedForPermissions
        .filter((askedPermission) => Boolean(askedPermission.resourceId))
        .map((permission) => permission.module),
    [askedForPermissions],
  )

  const hasLoadedSystemPermissionsForClinic = useMemo(
    () =>
      Array.from(systemPermissions.keys()).some((key) =>
        key.endsWith(`-${clinicId}`),
      ),
    [clinicId, systemPermissions],
  )

  const hasDataForAllPermissions = useMemo(
    () =>
      requestedContentPermissionsModules.every(
        (module) =>
          permissionsPerModuleMap?.get(getModuleMapKey(clinicId, module))?.size,
      ) && hasLoadedSystemPermissionsForClinic,
    [
      clinicId,
      hasLoadedSystemPermissionsForClinic,
      permissionsPerModuleMap,
      requestedContentPermissionsModules,
    ],
  )

  const generalSrn = generateBaseClinicSrn(clinicId)
  const srns = generateUniqueRequestedSrns(
    clinicId,
    generalSrn,
    askedForPermissions,
  )

  const enabled = !skip && Boolean(clinicId) && !hasDataForAllPermissions
  const {
    data,
    isLoading,
    isSuccess,
    refetch: refetchPermissions,
  } = useEndpoint<AccessPermissionsResponse>(
    srns,
    getPermissions(apiVersion, Number(clinicId), srns),
    {
      opts: {
        enabled,
      },
    },
  )

  useEffect(() => {
    if (isSuccess) {
      resetPermissionsMapsIfNeeded(clinicId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, clinicId])

  useEffect(() => {
    const hasSavedSystemPermissionsForClinic = Array.from(
      systemPermissions.keys(),
    ).some((moduleKey) => {
      const { moduleClinicId } = parseModuleMapKey(moduleKey)

      return Number(moduleClinicId) === clinicId
    })

    if (hasSavedSystemPermissionsForClinic || !data?.access) {
      return
    }

    const hasAnySystemPermissionInResponse = data.access.some((access) =>
      access.srns.includes(generalSrn),
    )

    const permissionsPerModule = hasAnySystemPermissionInResponse
      ? generateNewMapOfSystemPermissions({
          data,
          generalSrn,
        })
      : generateMapForEmptySystemPermissions({ clinicId })

    addSystemPermissions(clinicId, permissionsPerModule)
  }, [data, clinicId, generalSrn, systemPermissions, addSystemPermissions])

  useEffect(() => {
    if (
      !data?.access?.length ||
      data?.access?.every((access) => access.srns.includes(generalSrn))
    ) {
      return
    }

    askedForPermissions.forEach((askedPermission) => {
      const moduleContentPermissionsMap = getPermissionsForModule(
        clinicId,
        askedPermission.module,
      )

      const askedPermissionSrn = generateSrn(clinicId, askedPermission)
      const askedPermissionMap =
        moduleContentPermissionsMap?.get(askedPermissionSrn)
      const askedPermissionLoadedData = data?.access?.find((access) =>
        access.srns.includes(askedPermissionSrn),
      )

      if (
        !moduleContentPermissionsMap ||
        moduleContentPermissionsMap.size === 0 ||
        (!askedPermissionMap && askedPermissionLoadedData)
      ) {
        const newMap = generateNewMapOfContentPermissions({ data, generalSrn })
        addPermissionsForModule(clinicId, askedPermission.module, newMap)
      }
    })
  }, [
    data,
    clinicId,
    addPermissionsForModule,
    getPermissionsForModule,
    askedForPermissions,
    generalSrn,
  ])

  const hasPermission = useMemo(() => {
    const permissionsGranted = askedForPermissions.map((askedPermission) => {
      const isAskingResourceContentPermission = Boolean(
        askedPermission.resourceId,
      )
      const requiredPermission = getPermission(askedPermission)
      const isAskingForViewPermission =
        askedPermission.action === PermissionAction.VIEW ||
        askedPermission.action === PermissionAction.VIEW_ALL_SURVEYOR

      if (isAskingForViewPermission && !isAskingResourceContentPermission) {
        const hasBeenGrantedThroughViewSystemOverride =
          hasAnyResourceInModuleWithViewPermission({
            data,
            module: askedPermission.module,
            requiredPermission,
            clinicId,
            getPermissionsForModule,
          })

        if (hasBeenGrantedThroughViewSystemOverride) {
          return hasBeenGrantedThroughViewSystemOverride
        }
      }

      const hasBeenGrantedThroughAllResourcesSrn =
        checkPermissionGrantedThroughAllResourcesPermission({
          clinicId,
          data,
          askedPermission,
          getPermissionsForModule,
        })

      if (hasBeenGrantedThroughAllResourcesSrn) {
        return hasBeenGrantedThroughAllResourcesSrn
      }

      const result = !isAskingResourceContentPermission
        ? hasSystemPermission({
            module: askedPermission.module,
            requiredPermission,
            clinicId,
            getSystemPermissionsForModule,
          })
        : hasContentPermission({
            clinicId,
            askedPermission,
            requiredPermission,
            data,
            getPermissionsForModule,
          })

      // log('permission', askedPermission, {
      //   isAskingResourceContentPermission,
      //   isAskingForViewPermission,
      //   result,
      // })

      return result
    })

    return logic === AuthPermissionsLogicType.OR
      ? permissionsGranted.some((granted) => granted)
      : permissionsGranted.every((granted) => granted)
  }, [
    clinicId,
    askedForPermissions,
    data,
    logic,
    getPermissionsForModule,
    getSystemPermissionsForModule,
  ])

  log({
    askedForPermissions,
    hasDataForAllPermissions,
    hasPermission,
    srns,
    permissionsPerModuleMap,
    systemPermissions,
  })

  /**
   * if the request is loading, or data has not been initialized,
   * or we simple need to force skip validation, returns value provided via "defaultsTo" prop
   *
   * hasDataForAllPermissions has to be added, otherwise we would run into cases where isLoading is false,
   * but the data has not been set either
   */
  if (isLoading || skip || !hasDataForAllPermissions) {
    return {
      isLoadingPermissions: isLoading,
      hasPermission: defaultsTo,
      fetchPermissionsForNewAddedResource: refetchPermissions,
    }
  }

  return {
    hasPermission,
    isLoadingPermissions: isLoading,
    fetchPermissionsForNewAddedResource: refetchPermissions,
  }
}
